<template>
  <div ref="target" class="tw-relative">
    <base-button
      v-if="tipHover"
      text-link
      auto-height
      :primary="primary && !warning"
      :warning="warning"
      icon="question-circle"
      @click="tipClick"
    />

    <template v-else>
      <slot />
    </template>

    <teleport :disabled="!fixed" to="body">
      <div
        v-if="text || $slots.toast || $slots.custom"
        ref="tooltip"
        class="tw-fixed tw-flex tw-items-center tw-text-xs tw-transition-opacity tw-text-left tw-w-max tw-max-w-60"
        :class="[
          hovered ? 'tw-opacity-100' : 'tw-opacity-0',
          fixed ? 'tw-z-40' : 'tw-z-10',
          {
            'tw-flex-col': pos.includes('top'),
            'tw-flex-col-reverse': pos.includes('bottom'),
            'tw-flex-row': pos === 'left',
            'tw-flex-row-reverse': pos === 'right',
            'tw-pointer-events-none': !show,
            'lg:tw-hidden': hideOnLg,
            'show-on-sm': showOnSm,
          },
          Classes.TOOLTIP,
        ]"
        :style="style"
      >
        <div
          class="tw-rounded tw-shadow-md tw-font-normal"
          :class="[
            backgroundClass,
            `tw-text-${background}-text`,
            extraPaddings ? 'tw-px-1 tw-py-2.5' : 'tw-py-1.5 tw-px-3',
            {
              'tw-whitespace-nowrap': noWrap,
            },
          ]"
        >
          {{ text }}

          <slot name="toast" />
          <slot name="custom" />
        </div>
        <font-awesome-icon
          icon="caret-up"
          :class="[
            textClass,
            {
              'tw-rotate-180 tw--translate-y-2.5': pos.includes('top'),
              'tw-translate-y-2.5': pos.includes('bottom'),
              'tw-rotate-90 tw--translate-x-1.5': pos === 'left',
              '-tw-rotate-90 tw-translate-x-1.5': pos === 'right',
            },
          ]"
          :style="caretStyle"
          size="2x"
        />
      </div>
    </teleport>
  </div>
</template>

<script>
import { watchEffect, computed, nextTick, toRefs, watch, ref } from 'vue'
import { useElementBounding, useEventListener } from '@vueuse/core'
import { SCROLL_TO_ID } from '@config/htmlIDs.js'
import Classes from '@shared/js/config/css.js'

const EDGE_MARGIN = 0

export default {
  props: {
    position: {
      type: String,
      default: 'top',
      validator: function (value) {
        return (
          [
            'top',
            'bottom',
            'left',
            'right',
            'top left',
            'top right',
            'bottom left',
            '',
          ].indexOf(value) !== -1
        )
      },
    },
    text: {
      type: String,
      default: '',
    },
    background: {
      type: String,
      default: 'primary',
    },
    show: {
      type: Boolean,
      default: false,
    },
    tipHover: {
      type: Boolean,
      default: false,
    },
    showOnSm: {
      type: Boolean,
      default: false,
    },
    hideOnLg: {
      type: Boolean,
      default: false,
    },
    primary: {
      type: Boolean,
      default: true,
    },
    warning: {
      type: Boolean,
      default: false,
    },
    extraPaddings: {
      type: Boolean,
      default: false,
    },
    autoPos: {
      type: Boolean,
      default: false,
    },
    fixed: {
      type: Boolean,
      default: false,
    },
    noWrap: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { slots }) {
    const { text, position, tipHover, autoPos, background } = toRefs(props)

    const hovered = ref(false)
    const caretStyle = ref({})
    const tooltip = ref(null)
    const target = ref(null)
    const style = ref({})

    const { top } = useElementBounding(target)

    const pos = computed(() => (autoPos.value ? getAutoPos() : position.value))

    function positionTooltip() {
      if (!tooltip.value) return

      const targetRect = target.value.getBoundingClientRect()
      const distToLeft = targetRect.left
      const distToRight = window.innerWidth - targetRect.right

      const tooltipWidth = tooltip.value.offsetWidth
      const tooltipHeight = tooltip.value.offsetHeight

      let left = 0
      style.value = {}

      // Vertical position
      if (pos.value.includes('top')) {
        style.value.translate = '0 -0.25rem'
        style.value.top = `${targetRect.top - tooltipHeight + 16}px`
      } else if (pos.value.includes('bottom')) {
        style.value.translate = '0 -0.75rem'
        style.value.top = `${targetRect.bottom}px`
      } else {
        style.value.top = `${targetRect.top + targetRect.height / 2 - tooltipHeight / 2}px`
      }

      // Horizontal position
      if (pos.value === 'left') {
        left = targetRect.left - tooltipWidth
      } else if (pos.value === 'right') {
        left = targetRect.right
      } else {
        // Center tooltip
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2

        // Check if tooltip is off screen
        // Right edge
        let overlap = -targetRect.width / 2 + tooltipWidth / 2 - distToRight
        if (overlap > 0) {
          left -= overlap + EDGE_MARGIN
          caretStyle.value = {
            '--tw-translate-x': `${overlap + EDGE_MARGIN}px`,
          }
        } else {
          // Left edge
          overlap = distToLeft + targetRect.width / 2 - tooltipWidth / 2
          if (overlap < 0) {
            left -= overlap - EDGE_MARGIN
            caretStyle.value = {
              '--tw-translate-x': `${overlap - EDGE_MARGIN}px`,
            }
          }
        }
      }

      style.value.left = `${left}px`
    }

    const backgroundClass = computed(() => {
      switch (background.value) {
        case 'success':
        case 'warning':
        case 'danger':
        case 'info':
          return `tw-bg-${background.value}`

        case 'primary':
        default:
          return 'game-primary-button-color game-primary-text'
      }
    })

    const textClass = computed(() => {
      switch (background.value) {
        case 'success':
        case 'warning':
        case 'danger':
        case 'info':
          return `tw-text-${background.value}`

        case 'primary':
        default:
          return 'tw-text-[var(--primary-button-color)]'
      }
    })

    function onHover() {
      hovered.value = true
    }

    function onUnhover() {
      hovered.value = false
    }

    function tipClick() {
      hovered.value = !hovered.value
    }

    function getAutoPos() {
      return top.value - (tooltip.value?.offsetHeight ?? 0) < 100
        ? 'bottom'
        : 'top'
    }

    useEventListener(target, 'mouseenter', onHover)
    useEventListener(target, 'mouseleave', onUnhover)
    useEventListener(
      document.getElementById(SCROLL_TO_ID),
      'scroll',
      positionTooltip,
      { capture: false, passive: true }
    )

    watchEffect(() => {
      if (hovered.value) {
        positionTooltip()
      }
    })

    watch(text, (_text) => {
      if (_text || slots.custom || slots.toast) nextTick(positionTooltip)
      else hovered.value = false
    })

    if (!tipHover.value && slots.default) {
      watch(slots.default, () => {
        if (hovered.value) {
          // close any tootlips if slot data changes
          hovered.value = false
        }
      })
    }

    return {
      backgroundClass,
      caretStyle,
      textClass,
      tipClick,
      hovered,
      Classes,
      tooltip,
      target,
      style,
      pos,
    }
  },
}
</script>

<style scoped>
@media (max-width: 767px) {
  .show-on-sm {
    display: none;
  }
}
</style>
